import { defineStore } from 'pinia'
import { httpClient } from '../shared/services/http-client'

interface SidebarPageItem {
  id: number
  display_name: string
  page_name: string
  report_id: number
  report__name: string
  report__codename: string
  display_order: number
  is_active: boolean
}

interface SidebarChild {
  name: string
  displayName: string
  meta: {
    icon?: string
    isPowerBI: boolean
    defaultCodename: string
    defaultName: string
    pageName: string
  }
}

// Maps report codename to Vue route name
const CODENAME_TO_ROUTE: Record<string, string> = {
  'user': 'user-dashboard',
  'leads': 'leads-dashboard',
  'leaderadmin': 'leads-admin-dashboard',
  'hr': 'hr-dashboard',
  'hradmin': 'hr-admin-dashboard',
}

// Maps report codename to default display name for the route
const CODENAME_TO_DEFAULT_NAME: Record<string, string> = {
  'user': 'User-Dashboard',
  'leads': 'Leads-Dashboard',
  'leaderadmin': 'Leads-Admin-Dashboard',
  'hr': 'HR-Dashboard',
  'hradmin': 'HR-Admin-Dashboard',
}

export const useSidebarStore = defineStore('sidebar', {
  state: () => ({
    sidebarPagesMap: {} as Record<string, SidebarChild[]>,
    loaded: false,
    loading: false,
  }),

  getters: {
    // Must be a getter (not an action) so Vue's reactivity system tracks
    // sidebarPagesMap access and re-evaluates computed properties when it changes
    getChildrenForCodename: (state) => {
      return (codename: string): SidebarChild[] => {
        return state.sidebarPagesMap[codename] || []
      }
    },
  },

  actions: {
    async fetchSidebarPages(entity_id: string) {
      if (this.loading) return
      this.loading = true

      try {
        const url = `/reportPagesList?entity_id=${entity_id}`
        const response = await httpClient.get(url)
        const data = response.data
        const pages: SidebarPageItem[] = data.reportPages || []

        // Group by report codename and map to SidebarChild format
        const grouped: Record<string, SidebarChild[]> = {}

        for (const page of pages) {
          const codename = page.report__codename
          const routeName = CODENAME_TO_ROUTE[codename]
          const defaultName = CODENAME_TO_DEFAULT_NAME[codename]

          if (!routeName) {
            console.warn('No route mapping for codename:', codename)
            continue
          }

          if (!grouped[codename]) {
            grouped[codename] = []
          }

          grouped[codename].push({
            name: routeName,
            displayName: page.display_name,
            meta: {
              isPowerBI: true,
              defaultCodename: codename,
              defaultName: defaultName || page.report__name,
              pageName: page.page_name,
            },
          })
        }

        this.sidebarPagesMap = grouped
        this.loaded = true
        console.log('Sidebar pages loaded:', this.sidebarPagesMap)
      } catch (error) {
        console.error('Failed to fetch sidebar pages:', error)
        // On error, keep empty map — sidebar will show parents without children
        this.sidebarPagesMap = {}
      } finally {
        this.loading = false
      }
    },

    reset() {
      this.sidebarPagesMap = {}
      this.loaded = false
      this.loading = false
    },
  },
})
